import React from 'react'
import {
  CandidateReport2,
  FocusAreaResult,
} from '../apis/entities/report.entity'
import 'reactflow/dist/base.css'
import { Chart } from 'react-google-charts'
import '../components/charts/styles.css'
import {
  Assessment2,
  FrameworkLevel,
} from '../apis/entities/assessment.entity'
import ReactLoading from 'react-loading'
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js'
import { Radar } from 'react-chartjs-2'
import {
  formatSentence,
  removeDuplicates,
  stringSplitter,
} from '../utils/stringUtils'
import { findLowThresholdByStandardDeviation } from '../utils/statsUtils'
import { Footer } from './AssessmentIndividualReport'

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
)

type AssessmentIndividualReportProps = {
  candidateId: string
  assessment: Assessment2
  data: CandidateReport2
  testDate: string
  logo: string
  showAllOutcomes: boolean
}

export default function ReportContent2({
  candidateId,
  assessment,
  data,
  testDate,
  logo,
  showAllOutcomes,
}: AssessmentIndividualReportProps) {

  //meta data
  const bottomLevel = assessment.framework.levels[0].level
  const topLevel = assessment.framework.levels.slice(-1)[0].level
  const allCapabilityResults = data?.result.focusAreaResults
    .flatMap((focusAreaResult) => focusAreaResult.capabilityResults)
    .sort((a, b) => b.level! - a.level!)
  interface DisplayGrade {
    difficulty: string
    from: number
    to: number
  }
  const displayGrades: DisplayGrade[] = []
  for (let i = 0; i < assessment.framework.levels.length; i++) {
    const frameworkLevel = assessment.framework.levels[i]
    if (
      !displayGrades
        .map((l) => l.difficulty)
        .includes(frameworkLevel.difficulty)
    ) {
      displayGrades.push({
        difficulty: frameworkLevel.difficulty,
        from: frameworkLevel.level,
        to: frameworkLevel.level,
      })
    } else {
      const displayGrade = displayGrades.find(
        (l) => l.difficulty === frameworkLevel.difficulty,
      )
      if (displayGrade && frameworkLevel.level > displayGrade.to) {
        displayGrade.to = frameworkLevel.level
      }
    }
  }

  //page setup
  const contentWidth = 794 - 16 //page width - margin
  const modulePerPage = 2

  //charts
  const focusAreaNames = assessment.framework.areas.map((a) =>
    stringSplitter(a.title, 20),
  )
  // console.log('focusAreaNames', focusAreaNames)
  const focusAreaLevels =
    data?.result.focusAreaResults.map((r) => r.level) ?? []
  // console.log('focusAreaLevels', focusAreaLevels)
  const focusAreasRadarData = {
    //data?.result.focusAreaResults.map((r) => r.) ?? []
    labels: focusAreaNames,
    datasets: [
      {
        label: 'Your proficiency level',
        data: focusAreaLevels,
        backgroundColor: '#ffdc004d',
        borderColor: '#FFDC00',
        pointBackgroundColor: '#007882',
        pointBorderColor: '#007882',
        borderWidth: 1,
      },
    ],
  }

  const focusAreasRadarOptions = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 380 / 300, //aspect ratio by fixed width and height
    layout: {
      padding: 30,
    },
    scales: {
      r: {
        beginAtZero: true,
        suggestedMax: topLevel,
        grid: {
          color: '#1AC987',
        },
        angleLines: {
          color: '#1AC987',
        },
        ticks: {
          stepSize: Math.round(topLevel / 5), //5 lines distributed evenly
          fontSize: 40,
          color: '#000000',
          font: {
            size: 14,
            family: 'Arial',
          },
        },
        pointLabels: {
          font: {
            size: 14,
            weight: 'bold' as const,
            family: 'Arial',
          },
          color: '#000000',
          // callback: (label:any, index:any) => {
          //   return `Label ${label} ${index + 1}`
          // },
        },
      },
    },
    plugins: {
      legend: {
        position: 'top' as const,
        align: 'center' as const,
        labels: {
          font: { size: 20, weight: 'bold' as const, family: 'Clancy' },
          color: '#000000',
        },
        // display: false,
      },
    },
  }
  const capabilitiesRadarOptions = {
    responsive: true,
    aspectRatio: 380 / 300, //aspect ratio by fixed width and height
    maintainAspectRatio: true,
    layout: {
      padding: 30,
    },
    scales: {
      r: {
        beginAtZero: true,
        suggestedMax: topLevel,
        grid: {
          color: '#1AC987',
        },
        angleLines: {
          color: '#1AC987',
        },
        ticks: {
          stepSize: Math.round(topLevel / 5), //5 lines distributed evenly
          fontSize: 40,
          color: '#000000',
          font: {
            size: 12,
            family: 'Arial',
          },
        },
        pointLabels: {
          font: {
            size: 12,
            family: 'Arial',
          },
          color: '#000000',
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
    },
  }

  const FocusAreaDetails = ({
    focusAreaResult,
    index,
  }: {
    focusAreaResult: FocusAreaResult
    index: number
  }) => {
    const focusAreaLevelsIndex = assessment.framework.areas.find(
      (a) => a.index === focusAreaResult.focusArea,
    )?.index
    const focusAreaTitle = assessment.framework.areas.find(
      (a) => a.index === focusAreaResult.focusArea,
    )?.title

    //in the focus area
    const capabilityResults = focusAreaResult.capabilityResults.map(
      (capabilityResult) => capabilityResult,
    )

    //in the focus area
    const capabilityNames = capabilityResults.map((capabilityResult) =>
      stringSplitter(
        assessment.framework.areas
          .find((area) => area.index === focusAreaResult.focusArea)
          ?.capabilities.find(
            (capability) => capability.index === capabilityResult.capability,
          )?.title || '',
        20,
      ),
    )
    const capabilityLevels =
      focusAreaResult.capabilityResults.map((r) => r.level) ?? []

    const capabilitiesRadarData = {
      //data?.result.focusAreaResults.map((r) => r.) ?? []
      labels: capabilityNames,
      datasets: [
        {
          label: 'Your proficiency level',
          data: capabilityLevels,
          backgroundColor: '#ffdc004d',
          borderColor: '#FFDC00',
          pointBackgroundColor: '#007882',
          pointBorderColor: '#007882',
          borderWidth: 1,
        },
      ],
    }
    const chartTextStyles = 'text-sm font-roboto'

    const sortedCapabilityResults = capabilityResults.sort(
      (a, b) => b.level! - a.level!,
    )

    //in the focus area
    const assessedRequirements = sortedCapabilityResults
      .flatMap((capabilityResult) => capabilityResult.proficiencyResults)
      .flatMap((proficiencyResult) =>
        proficiencyResult.requirementResults.flatMap(
          (requirementResult) => requirementResult,
        ),
      )
      .filter(
        (requirementResult) => requirementResult.questionAssessedCount > 0,
      )
    const failedRequirements = assessedRequirements
      .filter((requirementResult) => requirementResult.scoreRatio! < 1)
      .sort((a, b) => a.level! - b.level!)
    // console.log('failedRequirements', failedRequirements)

    //in the focus area
    const failedRequirementNames = removeDuplicates(
      failedRequirements.map(
        (failedRequirement) =>
          assessment.framework.areas
            .find(
              (frameworkArea) =>
                frameworkArea.index === focusAreaResult.focusArea,
            )
            ?.capabilities.find(
              (frameworkCapability) =>
                frameworkCapability.index === failedRequirement.capability,
            )
            ?.proficiencies.find(
              (frameworkCapability) =>
                frameworkCapability.level === failedRequirement.level,
            )
            ?.requirements.find(
              (r, rIndex) => rIndex + 1 === failedRequirement.requirement,
            ) ||
          `${failedRequirement.focusArea}.${failedRequirement.capability}.${failedRequirement.level}.${failedRequirement.requirement}`,
      ),
    )

    const levels: number[] = capabilityResults.map((r) => r.level!)

    //find capability lower than 1 standard deviation
    const lowThreshold = findLowThresholdByStandardDeviation(levels, 1)
    const badResults =
      sortedCapabilityResults.filter((r) => r.level! < lowThreshold!) ?? []
    const goodResults = sortedCapabilityResults.filter(
      (r) =>
        r.level! >= Math.round((topLevel + bottomLevel) / 2) &&
        !badResults.includes(r),
    )

    const goodResultDescriptions =
      goodResults.length > 0
        ? formatSentence(
            `You demonstrated a high level of proficiency in the ${
              goodResults.length === 1 ? 'capabilitiy' : 'capabilities'
            }: ${goodResults.map(
              (r) =>
                `'` +
                assessment.framework.areas
                  .find((area) => area.index === focusAreaResult.focusArea)
                  ?.capabilities.find(
                    (capability) => capability.index === r.capability,
                  )?.title +
                `'`,
            )}.`,
          )
        : ''

    const badDescriptions =
      badResults.length > 0
        ? formatSentence(
            `${
              badResults.length === 1 ? 'Area' : 'Areas'
            } for improvement: ${badResults.map(
              (r) =>
                `'` +
                assessment.framework.areas
                  .find((area) => area.index === focusAreaResult.focusArea)
                  ?.capabilities.find(
                    (capability) => capability.index === r.capability,
                  )?.title +
                `'`,
            )}.`,
          )
        : ''
    // console.log('badDescriptions', badDescriptions)

    const frameworkLevel = assessment.framework.levels.find(
      (l) => Math.floor(l.level) === Math.floor(focusAreaResult.level!),
    )

    const areaSummary = formatSentence(
      `In the area ${focusAreaTitle}, you can ${frameworkLevel?.complexity}.`,
    )

    return (
      <>
        <div className="text-2xl font-bold">
          3.{index + 1} {focusAreaTitle}
        </div>
        <div className="flex flex-row gap-4 w-[776px] items-center pt-6">
          <div className={`min-w-[400px]`}>
            <Radar
              data={capabilitiesRadarData}
              options={{
                ...capabilitiesRadarOptions,
                // maintainAspectRatio: false,
              }}
              width={380}
              height={300}
            />
          </div>
          <div className={chartTextStyles + ' '}>
            <div>{areaSummary}</div>
            <div className="pt-2">
              {goodResultDescriptions && goodResultDescriptions}
            </div>
            <div className="pt-2">{badDescriptions && badDescriptions}</div>
            {failedRequirementNames.length > 0 && (
              <div className="pt-2">
                Recommended learning targets:
                <ul style={{ listStyleType: 'disc' }} className="ml-4">
                  {failedRequirementNames.map(
                    (r, index) =>
                      index < 5 && (
                        <li key={`targetRequirement-${index}`}>{r}</li>
                      ),
                  )}
                </ul>
              </div>
            )}
          </div>
        </div>
      </>
    )
  }

  //page render variables
  const subModulePages = []
  const subModuleCount = focusAreaNames.length + 1 // charts + conclusion
  const subModulePageCount = Math.ceil(subModuleCount / modulePerPage)
  for (let i = 0; i < subModulePageCount; i++) {
    subModulePages.push(i)
  }

  //render summary
  const Summary = () => {
    const levels: number[] =
      data?.result.focusAreaResults.map((r) => r.level! ?? bottomLevel) ?? []

    const frameworkLevel = assessment.framework.levels.find(
      (l) => l.level === Math.floor(data?.result.level!),
    )

    //find focus areas lower than 1 standard deviation
    const lowThreshold = findLowThresholdByStandardDeviation(levels, 1)
    const badResults =
      data?.result.focusAreaResults.filter((r) => r.level! < lowThreshold!) ??
      []
    const goodResults =
      data?.result.focusAreaResults.filter(
        (r) =>
          r.level! >= Math.round((topLevel + bottomLevel) / 2) &&
          !badResults.includes(r),
      ) ?? []

    const goodResultDescriptions =
      goodResults.length > 0
        ? formatSentence(
            `You performed well in the ${
              goodResults.length === 1 ? 'area' : 'areas'
            }: ${goodResults.map(
              (focusAreaResult) =>
                `` +
                assessment.framework.areas.find(
                  (area) => area.index === focusAreaResult.focusArea,
                )?.title +
                ``,
            )}. `,
          )
        : ''

    const badDescriptions =
      badResults.length > 0
        ? formatSentence(
            `Potential areas for improvement are: ${badResults.map(
              (focusAreaResult) =>
                `'` +
                assessment.framework.areas.find(
                  (area) => area.index === focusAreaResult.focusArea,
                )?.title +
                `'`,
            )}. `,
          )
        : ''

    // const summary =
    //   formatSentence(`The overall level is '${frameworkLevel?.difficulty}'. `) +
    //   formatSentence(
    //     `You demonstrated the capabilities to ${frameworkLevel?.complexity}. `,
    //   )

    return (
      <>
        <div className="font-roboto">
          <div className="text-2xl font-bold">2. Summary</div>
          <div className="mt-6">
            {goodResultDescriptions}
            {badDescriptions}
            <div className="mt-2">
              Your overall level is{' '}
              <span className="font-bold text-tertiaryTeal">
                {frameworkLevel?.difficulty.toLocaleLowerCase()}
              </span>
              .
            </div>
            <div className="mt-2">
              You demonstrated the capabilities to{' '}
              {frameworkLevel?.complexity.toLocaleLowerCase()}.
            </div>
          </div>
        </div>
      </>
    )
  }

  //render conclusions
  const Conclusions = () => {
    const frameworkLevel = assessment.framework.levels.find(
      (l) => l.level === Math.floor(data?.result.level!),
    )

    const highestCapabilityLevel = allCapabilityResults?.slice(0, 1)[0].level
    const topCapabilities = allCapabilityResults?.filter(
      (capabilityResult) => capabilityResult.level === highestCapabilityLevel,
    )
    const top3Capabilities = topCapabilities?.slice(0, 3)

    let nextLevel = Math.floor(frameworkLevel?.level! + topLevel / 4)
    if (nextLevel > topLevel) {
      nextLevel = topLevel
    }
    const nextFrameworkLevel = assessment.framework.levels.find(
      (l) => Math.ceil(l.level) === nextLevel,
    )

    return (
      <>
        <div className="font-roboto py-3">
          <div className="text-2xl font-bold">4. Conclusions</div>

          {/* current level */}
          <div className="mt-4">
            Your current level in{' '}
            <span className="font-bold">{assessment.program}</span> is{' '}
            <span className="font-bold">
              {frameworkLevel?.difficulty.toLocaleLowerCase()}
            </span>
            .
          </div>

          <div className="mt-2">
            {formatSentence(
              `${
                top3Capabilities?.length === 1
                  ? 'The strongest capability is: '
                  : 'Some of your strongest capabilities are: '
              } ${top3Capabilities?.map(
                (capabilityResult, index) =>
                  `'` +
                  assessment.framework.areas
                    .find((area) => area.index === capabilityResult.focusArea)
                    ?.capabilities.find(
                      (capability) =>
                        capability.index === capabilityResult.capability,
                    )?.title +
                  `'`,
              )}. `,
            )}
          </div>

          {/* has next level */}
          {nextLevel < topLevel && (
            <div className="mt-2">
              {`As you continue your learning journey at the `}
              <span className="font-bold">
                {nextFrameworkLevel?.difficulty.toLocaleLowerCase()}
              </span>
              {` level in `}
              <span className="font-bold">{assessment.program}</span>
              {`,
              you will progressively develop the skills and knowledge necessary
              to ${nextFrameworkLevel?.complexity.toLocaleLowerCase()}.`}
            </div>
          )}
          <div className="mt-2">
            Learn more about UNSW’s short courses and professional development
            at{' '}
            <a
              className=""
              href="https://www.unsw.edu.au/study/professional-development"
              target="_blank"
              rel="opener noreferrer"
            >
              https://www.unsw.edu.au/study/professional-development
            </a>
            .
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <div className="flex grow relative w-[794px] h-[1142px] overflow-auto border-0 pl-2 pr-2 pt-3">
        <div className="flex grow flex-col gap-8 text-left">
          {data === undefined && (
            <ReactLoading
              className="fixed top-[50%]"
              type={'spinningBubbles'}
              color={'#eeeeee'}
              height={'64px'}
              width={'64px'}
            />
          )}

          {data && (
            <div className="">
              <div className="relative">
                {/* Header */}
                <div className="flex justify-between items-center gap-6">
                  {/* left logo*/}
                  <div className="self-start">
                    <img src={logo} alt="logo" className="w-[300px]" />

                    <div className="text-2xl font-bold pt-7">
                      {assessment?.title}
                    </div>
                    <div className="pt-3 font-roboto pb-6">
                      {assessment?.description}
                    </div>
                  </div>

                  <div className="flex flex-col gap-3">
                    <div className="text-[32px] font-bold">
                      {data?.firstName} {data?.lastName}
                    </div>
                    <div className="text-sm font-semibold font-roboto">
                      Test date: {testDate}
                    </div>
                    <div className="text-sm font-semibold font-roboto">
                      Result code: {data?.id}
                    </div>

                    {/* score box */}
                    <div className="flex flex-col h-38 text-center overflow-hidden border border-1 border-primaryUnsw mt-5 w-[300px]">
                      <div className="font-bold flex items-center justify-center text-tertiaryTeal text-[32px] py-3">
                        Your level
                      </div>
                      <div
                        className=""
                        style={{
                          background: 'rgba(255, 220, 0, 0.30)',
                        }}
                      >
                        <div className="text-[32px] text-center font-bold py-9">
                          {
                            assessment.framework.levels.find(
                              (l) =>
                                l.level === Math.floor(data?.result.level!),
                            )!.difficulty
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* overall chart */}
                <div className="">
                  <div className="text-2xl font-bold">1. Overview</div>
                  <div className="font-roboto pt-6 text-md">
                    <div>
                      <b>{assessment.title}</b> assesses the proficiencies of
                      your capabilities in <b>{assessment.program}</b> in{' '}
                      <b>{assessment.framework.areas.length}</b> areas:
                    </div>
                    <div className="mt-2">
                      {assessment.framework.areas
                        .map((area, index) => `${area.title}`)
                        .join(', ')}
                      .
                    </div>
                    <div className="mt-2">
                      The following chart shows your proficiency levels in each
                      focus area:
                    </div>
                  </div>

                  <div className="flex justify-between mt-2 max-w-[778px]">
                    <div className="flex-1 w-full">
                      <Radar
                        data={focusAreasRadarData}
                        options={focusAreasRadarOptions}
                      />
                    </div>
                    {/* grade maps */}
                    <div className="flex flex-col gap-12 justify-center">
                      {displayGrades.map((displayGrade, index) => (
                        <div
                          className="text-sm font-bold text-tertiaryTeal"
                          key={`displayGrade-${index}`}
                        >
                          <div>
                            {`Level ${displayGrade.from} to ${displayGrade.to}: `}
                          </div>
                          <div className="text-black">
                            {displayGrade.difficulty}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <Footer />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* page2 */}
      {subModulePages.map((subModulePageIndex) => (
        <div
          key={`subPage-${subModulePageIndex}`}
          className="mt-8 flex grow relative w-[794px] h-[1142px] overflow-auto border-0 pl-2 pr-2 pt-3"
        >
          <div className="flex grow flex-col gap-8 text-left">
            {data && (
              <div className="">
                <div className="relative">
                  {/* Header */}

                  {subModulePageIndex === 0 && (
                    <>
                      <Summary />
                      <div className="text-2xl font-bold pt-6">
                        3. Capability details
                      </div>
                    </>
                  )}
                  <div className="pt-6">
                    {/* charts */}
                    {data?.result.focusAreaResults.map(
                      (focusAreaResult, index) =>
                        //modeulePage 0 -> 0,1,2
                        index >= subModulePageIndex * modulePerPage &&
                        index < (subModulePageIndex + 1) * modulePerPage && (
                          <FocusAreaDetails
                            focusAreaResult={focusAreaResult}
                            index={index}
                            key={`fucusArea-${index}`}
                          />
                        ),
                    )}
                    {/* end charts */}

                    {/* only in last module page and there is empty space*/}
                    {subModulePageIndex === subModulePages.length - 1 && (
                      <Conclusions />
                    )}
                  </div>
                  <Footer />
                </div>
              </div>
            )}
          </div>
        </div>
      ))}
    </>
  )
}
